import { supabase } from '../lib/supabase';

export interface CrimeData {
  codeInsee: string;
  commune: string;
  annee: number;
  cambriolages: number;
  tentativesCambriolages: number;
  tauxPour1000Logements: number;
  statutDiffusion: 'diff' | 'ns';
}

export interface CrimeComparison {
  communeRate: number;
  departmentRate: number;
  nationalRate: number;
  percentileRank: number;
  decile: number;
  isAboveAverage: boolean;
  relativeDifference: number;
}

// Moyennes françaises réelles 2024 (sources officielles)
const NATIONAL_AVERAGE_RATE = 3.95; // Taux national moyen pour 1000 logements
const DEFAULT_DEPARTMENT_RATE = 5.0; // Fallback si données département indisponibles

/**
 * Récupère les statistiques de cambriolages depuis la table Supabase `crime_statistics`.
 * La table est la seule source de vérité — aucune API externe n'est sollicitée.
 */
export async function getCrimeStatistics(
  codeInsee: string,
  _communeName: string
): Promise<CrimeData | null> {
  if (!codeInsee || codeInsee.length !== 5) {
    console.error('Invalid INSEE code:', codeInsee);
    return null;
  }

  try {
    const { data, error } = await supabase
      .from('crime_statistics')
      .select('*')
      .eq('code_insee', codeInsee)
      .order('annee', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) {
      console.error('Error fetching crime statistics from DB:', error);
      return null;
    }

    if (!data) return null;

    return {
      codeInsee: data.code_insee,
      commune: data.commune_name || '',
      annee: data.annee,
      cambriolages: data.cambriolages,
      tentativesCambriolages: data.tentatives,
      tauxPour1000Logements: data.taux_pour_1000,
      statutDiffusion: data.statut_diffusion as 'diff' | 'ns'
    };
  } catch (error) {
    console.error('Unexpected error fetching crime statistics:', error);
    return null;
  }
}

export async function getDepartmentAverageRate(codeDepartement: string): Promise<number> {
  try {
    const { data, error } = await supabase
      .from('crime_statistics')
      .select('taux_pour_1000')
      .like('code_insee', `${codeDepartement}%`)
      .gt('taux_pour_1000', 0)
      .order('annee', { ascending: false })
      .limit(500);

    if (error || !data || data.length === 0) {
      return DEFAULT_DEPARTMENT_RATE;
    }

    const sum = data.reduce((acc: number, item: any) => acc + item.taux_pour_1000, 0);
    const average = sum / data.length;

    if (average > 0 && average < 50) {
      return average;
    }

    return DEFAULT_DEPARTMENT_RATE;
  } catch (error) {
    console.error('Error calculating department average:', error);
    return DEFAULT_DEPARTMENT_RATE;
  }
}

export async function getNationalAverageRate(): Promise<number> {
  return NATIONAL_AVERAGE_RATE;
}

async function getLatestYear(): Promise<number> {
  try {
    const { data, error } = await supabase
      .from('crime_statistics')
      .select('annee')
      .order('annee', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error || !data) {
      return new Date().getFullYear() - 1;
    }

    return data.annee;
  } catch (error) {
    return new Date().getFullYear() - 1;
  }
}

export async function getCrimeComparison(
  crimeData: CrimeData,
  codeDepartement: string
): Promise<CrimeComparison> {
  const [departmentRate, nationalRate] = await Promise.all([
    getDepartmentAverageRate(codeDepartement),
    getNationalAverageRate()
  ]);

  const communeRate = crimeData.tauxPour1000Logements;
  const relativeDifference = nationalRate > 0
    ? ((communeRate - nationalRate) / nationalRate) * 100
    : 0;

  const isAboveAverage = communeRate > nationalRate;

  // Le décile n'est plus utilisé dans la logique d'affichage
  // On le calcule quand même pour compatibilité mais il n'est pas affiché
  let percentileRank = 50;
  let decile = 5;

  try {
    const { data, error } = await supabase
      .from('crime_statistics')
      .select('taux_pour_1000')
      .gt('taux_pour_1000', 0)
      .order('taux_pour_1000', { ascending: true });

    if (!error && data && data.length > 10) {
      const rates = data.map((item: any) => item.taux_pour_1000);
      const position = rates.filter(rate => rate < communeRate).length;
      percentileRank = Math.round((position / rates.length) * 100);
      decile = Math.min(Math.ceil(percentileRank / 10), 10);
      if (decile === 0) decile = 1;
    }
  } catch (error) {
    console.error('Error calculating percentile:', error);
  }

  return {
    communeRate,
    departmentRate,
    nationalRate,
    percentileRank,
    decile,
    isAboveAverage,
    relativeDifference
  };
}